import { Component, EventEmitter, Output, TemplateRef, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked'; 
import { SharedService } from 'src/app/services/shared.service';
import { REPORT_LANGUAGES } from 'src/app/shared/helpers/data';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from 'src/app/services/auth.service';

@Component({
    selector: 'app-view-generated-report',
    templateUrl: './view-generated-report.component.html',
    styleUrls: ['./view-generated-report.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class ViewGeneratedReportComponent {
  @ViewChild('reportViewModal') reportViewModal!: TemplateRef<any>;
  @ViewChild('onAddLanguageModal') onAddLanguageModal!: TemplateRef<any>;
  @ViewChild('responseModal') responseModal!: TemplateRef<any>;

  @Output() updateReportsEvent = new EventEmitter<any>()

  // Data State
  reportData: any; 
  onAddLanguageModalRef: any;
  responseModalRef: any;
  currentLang: any;
  availableLanguages: any[] = [];
  
  // Cache System: Stores fetched content to avoid re-fetching
  // Key = Language Code (e.g., 'EN'), Value = Rendered HTML
  contentCache: { [key: string]: SafeHtml } = {};

  // UI State
  isLoading = false;
  errorMessage = '';
  statusMessage = ''; // For "Generating" or "Failed" states
  renderedContent: SafeHtml = '';
  isRtl = false;
  get_report_url: string = '';
  generate_report_url: string = '';
  get_entity_data_url: string = '';
  activity_type: string = '';

  languageList = REPORT_LANGUAGES;
  addLanguageForm!: FormGroup;
  filteredLanguageList: any[] = [];
  submitted = false;
  spinner = false;
  success = false;
  message: string = '';
  entity: any = null;

  refreshButtonDisabled = false;
  isRefreshing = false;
  refreshInterval: number = 10000; // 10 seconds

  constructor(private modalService: NgbModal, private formBuilder: FormBuilder, private sanitizer: DomSanitizer, private sharedService: SharedService, private authService: AuthService) {}

  /**
   * ENTRY POINT: Parent calls this function
   */
  openModal(data: any, get_report_url: string, activity_type: string, generate_report_url: string, get_entity_data_url: string) {
    this.generate_report_url = generate_report_url;
    this.get_report_url = get_report_url;
    this.get_entity_data_url = get_entity_data_url;
    this.activity_type = activity_type;
    this.reportData = data;
    this.contentCache = {};
    this.errorMessage = '';
    this.statusMessage = '';
    this.availableLanguages = data.languages || [];
    this.modalService.open(this.reportViewModal, { fullscreen: true, scrollable: true, backdrop: 'static', keyboard: false });

    if (this.availableLanguages.length > 0) {
      // const defaultLang = this.availableLanguages.find(l => l.code === 'EN') || this.availableLanguages[0];
      this.onLanguageChange(this.availableLanguages[0]);
    } else {
      this.errorMessage = "No languages available for this report.";
    }
  }

  get canAddLanguage(): boolean {
    // Return TRUE only if at least one report is strictly 'success'
    // This confirms the Master Draft exists and is safe to use.
    return this.availableLanguages.some(l => l.status && l.status.toLowerCase() === 'success');
  }

  onLanguageChange(lang: any) {
    if (!lang) return;
    this.currentLang = lang;
    this.checkDirection(lang.code);
    
    // Check Status first
    if (lang.status !== 'success') {
      this.handleNonSuccessStatus(lang);
      return;
    }

    // Check Cache
    if (this.contentCache[lang.code]) {
      this.renderedContent = this.contentCache[lang.code];
      this.errorMessage = '';
      this.statusMessage = '';
      return;
    }

    // Fetch from Backend
    this.fetchReportContent(lang);
  }

  fetchReportContent(lang: any) {
    this.isLoading = true;
    this.errorMessage = '';
    this.statusMessage = '';
    this.renderedContent = '';

    const payload = { file_path: lang.file_path };

    this.sharedService.getReport(this.get_report_url, payload, this.activity_type).subscribe({
      next: (response: any) => {
        const markdownText = response.content || response; 

        const rawHtml = marked.parse(markdownText) as string;
        const safeHtml = this.sanitizer.bypassSecurityTrustHtml(rawHtml);

        this.contentCache[lang.code] = safeHtml;
        this.renderedContent = safeHtml;
        
        this.isLoading = false;
      },
      error: (err: any) => {
        console.error('Error fetching report:', err);
        this.errorMessage = "Failed to load report content.";
        this.isLoading = false;
      }
    });
  }

  handleNonSuccessStatus(lang: any) {
    this.renderedContent = '';
    this.errorMessage = '';
    
    // Normalize status to lowercase so 'Processing' and 'processing' both work
    const status = lang.status ? lang.status.toLowerCase() : '';

    if (status === 'generating') {
      // Parse the number and fix to 2 decimal places
      const rawProgress = lang.progress ? parseFloat(lang.progress) : 0;
      const formattedProgress = parseFloat(rawProgress.toFixed(2));

      this.statusMessage = `⏳ This report is currently being generated. Please wait... ${formattedProgress}%`;
    } 
    else if (status === 'failed') {
      this.statusMessage = '⚠️ Report generation failed. Please try generating it again.';
      if (lang.error) {
        this.statusMessage += ` (${lang.error})`;
      }
    } 
    else {
      // Fallback for unexpected statuses
      this.statusMessage = `Status: ${lang.status}`;
    }
  }

  checkDirection(langCode: string) {
    const rtlLangs = ['HE', 'AR', 'FA', 'UR'];
    this.isRtl = rtlLangs.includes(langCode ? langCode.toUpperCase() : 'EN');
  }

  onAddLanguage() {
    this.addLanguageForm = this.formBuilder.group({
      language: [null, [Validators.required]]
    });

    const existingCodes = this.reportData.languages.map((l: any) => 
      typeof l === 'string' ? l : l.code
    );

    this.filteredLanguageList = REPORT_LANGUAGES.filter(lang => 
      !existingCodes.includes(lang.prefix)
    );

    this.onAddLanguageModalRef = this.modalService.open(this.onAddLanguageModal, { size: this.canAddLanguage ? 'sm' : 'lg', backdrop: 'static', keyboard: false });
  }

  onAddLanguageSubmit() {
    this.submitted = true;

    if (this.addLanguageForm.invalid) {
      return;
    }

    this.spinner = true;
    const selectedLang = this.addLanguageForm.get('language')?.value;

    const payload = {
      template: this.reportData.template, 
      protocols: [this.reportData.protocol], 
      video_context: this.reportData.video_context, 
      header_data: this.reportData.header_data, 
      languages: [selectedLang]
    };

    this.sharedService.generateReport(this.generate_report_url, payload, this.activity_type).subscribe({
			next: (data) => {
        this.submitted = false;
        this.spinner = false;
        this.onAddLanguageModalRef.close();
        this.success = true;
        this.message = data.message;
        this.entity = data.entity;
      	this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			},
			error: (err) => {
				this.submitted = false;
				this.spinner = false;
				this.success = false;
        this.onAddLanguageModalRef.close();
        this.message = err.message;
        this.responseModalRef = this.modalService.open(this.responseModal, { size: 'lg', backdrop: 'static', keyboard : false });
			}
		});
  }

  async closeModal() {
		if (this.success) {
			this.updateReportsEvent.emit({ entity: this.entity });
      const reportsList = this.entity.extra_content?.reports || [];
      const updatedReport = reportsList.find((r: any) => r.id === this.reportData.id);

      if (updatedReport) {        
        this.reportData = updatedReport;
        this.availableLanguages = this.reportData.languages || [];
        const addedLangPrefix = this.addLanguageForm.get('language')?.value?.prefix;
        
        if (addedLangPrefix) {
          const newLangObj = this.availableLanguages.find(l => l.code === addedLangPrefix);
          if (newLangObj) {
            this.onLanguageChange(newLangObj);
          }
        }
      }
		}
		
		this.responseModalRef.close();
	}

  refreshReports() {
    if (this.refreshButtonDisabled) {
      return; 
    }

    this.refreshButtonDisabled = true;
		this.isRefreshing = true;

    this.sharedService.getEntity(this.get_entity_data_url, this.activity_type).subscribe({
      next: (data) => {
        if (data.activity_data) {
          this.entity = data.activity_data;
        } else {
          if (this.activity_type === 'examinee') {
            this.entity = data.examinee;
          } else {
            this.entity = data;
          }
        }

        this.updateReportsEvent.emit({ entity: this.entity });

        const reportsList = this.entity.extra_content?.reports || [];
        const updatedReport = reportsList.find((r: any) => r.id === this.reportData.id);

        if (updatedReport) {
           this.reportData = updatedReport;
           this.availableLanguages = this.reportData.languages || [];
           
           if (this.currentLang && this.currentLang.status === 'generating') {
              const updatedLang = this.availableLanguages.find(l => l.code === this.currentLang.code);
              if (updatedLang) {
                this.onLanguageChange(updatedLang); 
              }
           }
        }

        this.isRefreshing = false;
        this.authService.toastSuccessMessage('', 'Reports Refreshed');
      },
      error: (err) => {
        this.isRefreshing = false;
        this.success = false;
        this.authService.toastErrorMessage('Error refreshing data. Please try again.', 'Error refresh');
      }
    });

    setTimeout(() => {
			this.refreshButtonDisabled = false; // Re-enable button
		}, this.refreshInterval);
  }

  printReport() {
    window.print();
  }
}